import React, { createContext, SVGProps, useContext, useState } from 'react';
import {
  SelectableAnimationDirectionOption,
  SelectableAnimationPositionOption,
  selectableDrawTypeOptions,
  selectableEntranceAnimationOptions,
  selectableShakeAnimationOptions,
  selectableSpinDirectionOptions
} from 'js/config/animationOptions';
import {
  ENTRANCE_STAGE_KEY,
  EMPHASIS_STAGE_KEY,
  EXIT_STAGE_KEY,
  REVERSE_ICON_LIST,
  DEFAULT_HAND_CURSOR_ID,
  DEFAULT_PEN_CURSOR_ID,
  DEFAULT_DRAG_CURSOR_ID,
  DEFAULT_ERASE_CURSOR_ID,
  NO_CURSOR_ID
} from 'js/config/consts';
import { getStageTweenKey } from 'js/shared/helpers/getStageTweenKey';
import IconIndeterminate from 'js/shared/icons/IconIndeterminate';
import {
  AllAnimationTypeKeys,
  AnimationTypeOption,
  CursorOption,
  DrawType,
  ElementAnimationStage,
  ElementChoiceButtonProps,
  ElementMenuProps,
  ScribeCursorId,
  TransitionSubMenu
} from 'js/types';
import { useTypedSelector } from 'js/selectors/typedUseSelector';
import { findCursorById } from 'js/selectors/findCursorById';
import defaultHand from 'imgs/hands/Rosie1.png';
import defaultDrag from 'imgs/hands/default-drag.png';
import defaultPen from 'imgs/hands/default-pen.png';
import defaultEraseHand from 'imgs/hands/default-erase-1.png';

import { ChoiceButtonIcon } from './ChoiceButtonIcon';
import { getCursorIcon } from './helpers/getCursorIcon';

export type ValueChangeParams = {
  value: number | string;
  eventTrigger?: string;
  incrementValue?: boolean;
  decrementValue?: boolean;
};

export type AnimationTypeChangeParams = {
  animationType: AllAnimationTypeKeys;
  stageKey: ElementAnimationStage;
};

export type MenuConfig = {
  anchor: Element;
  primary: boolean;
  secondary: boolean;
  tertiary: boolean;
};

type GetSelectedChoiceLabelAndIcon = () => {
  label: string;
  icon: React.JSX.Element;
};

const noop = () => {};
const defaultChoiceLabelAndIcon = () => ({
  label: '',
  icon: <IconIndeterminate />
});

const AnimationStageContext = createContext<{
  animationType?: AllAnimationTypeKeys;
  animationTypeOptions: Array<AnimationTypeOption<ElementChoiceButtonProps, ElementMenuProps>>;
  animationTypeTitle: string;
  closeMenu: () => void;
  currentSelectedCursor: ScribeCursorId | undefined;
  currentSelectedDrawType: DrawType | 'mixed';
  currentSelectedSpinValue: number | undefined;
  directionMenuOptions: SelectableAnimationPositionOption[] | SelectableAnimationDirectionOption[];
  duration: number | string;
  getSelectedAnimationCursorChoice: GetSelectedChoiceLabelAndIcon;
  getSelectedCursorChoice: GetSelectedChoiceLabelAndIcon;
  getSelectedDirectionChoice: GetSelectedChoiceLabelAndIcon;
  getSelectedDrawTypeChoice: GetSelectedChoiceLabelAndIcon;
  getSelectedSpinChoice: GetSelectedChoiceLabelAndIcon;
  Icon: React.FC<{ flipImage?: boolean }> | React.FC<SVGProps<SVGSVGElement> & { title?: string | undefined }>;
  loops: number | string;
  menuOpen: MenuConfig | null;
  onAnimationCursorChoice: (cursorId: ScribeCursorId) => void;
  onAnimationTypeChange: (newAnimationType: AllAnimationTypeKeys) => void;
  onCursorChoice: (cursorId: ScribeCursorId) => void;
  onDirectionChoice: (options: SelectableAnimationPositionOption | SelectableAnimationDirectionOption) => void;
  onDrawTypeChoice: (drawType: DrawType) => void;
  onDurationChange: (params: ValueChangeParams) => void;
  onLoopsChange?: (params: ValueChangeParams) => void;
  onSpinDirectionChoice: (spinRotation: number) => void;
  reverseIcons: boolean;
  secondaryMenu: TransitionSubMenu<ElementChoiceButtonProps, ElementMenuProps> | undefined;
  setMenuOpen: React.Dispatch<React.SetStateAction<MenuConfig | null>>;
  showDuration: boolean;
  stageKey?: typeof ENTRANCE_STAGE_KEY | typeof EMPHASIS_STAGE_KEY | typeof EXIT_STAGE_KEY;
  tertiaryMenu: TransitionSubMenu<ElementChoiceButtonProps, ElementMenuProps> | undefined;
}>({
  animationType: undefined,
  animationTypeOptions: [],
  animationTypeTitle: '',
  closeMenu: noop,
  currentSelectedCursor: undefined,
  currentSelectedDrawType: 'mixed',
  currentSelectedSpinValue: undefined,
  directionMenuOptions: [],
  duration: '-',
  getSelectedAnimationCursorChoice: defaultChoiceLabelAndIcon,
  getSelectedCursorChoice: defaultChoiceLabelAndIcon,
  getSelectedDirectionChoice: defaultChoiceLabelAndIcon,
  getSelectedDrawTypeChoice: defaultChoiceLabelAndIcon,
  getSelectedSpinChoice: defaultChoiceLabelAndIcon,
  Icon: () => <IconIndeterminate />,
  loops: '-',
  menuOpen: null,
  onAnimationCursorChoice: noop,
  onAnimationTypeChange: noop,
  onCursorChoice: noop,
  onDirectionChoice: noop,
  onDrawTypeChoice: noop,
  onDurationChange: noop,
  onLoopsChange: noop,
  onSpinDirectionChoice: noop,
  reverseIcons: false,
  secondaryMenu: undefined,
  setMenuOpen: noop,
  showDuration: false,
  stageKey: undefined,
  tertiaryMenu: undefined
});

const rosieDefaultCursor = {
  group: 'Rosie',
  name: 'Rosie black fineliner',
  id: DEFAULT_HAND_CURSOR_ID,
  resources: { thumbnail: { url: defaultHand } }
};
const penDefaultCursor = {
  group: 'pens',
  name: 'Fountain Pen',
  id: DEFAULT_PEN_CURSOR_ID,
  resources: { thumbnail: { url: defaultPen } }
};
const dragDefaultCursor = {
  group: 'Rosie',
  name: 'Default',
  id: DEFAULT_DRAG_CURSOR_ID,
  resources: { thumbnail: { url: defaultDrag } }
};
const defaultEraseCursor: CursorOption = {
  group: 'Rosie',
  name: 'Default',
  id: DEFAULT_ERASE_CURSOR_ID,
  resources: { thumbnail: { url: defaultEraseHand } }
};

export const AnimationStageContextProvider = ({
  animationType,
  animationTypeOptions,
  children,
  currentSelectedAnimationCursor,
  currentSelectedCursor,
  currentSelectedDrawType,
  currentSelectedSpinValue,
  currentSelectedDirectionOption,
  defaultSecondaryMenu,
  duration = '-',
  loops,
  onAnimationCursorChoice,
  onAnimationTypeChange,
  onCursorChoice,
  onDirectionChoice,
  onDrawTypeChoice,
  onDurationChange,
  onLoopsChange,
  onSpinDirectionChoice,
  showDuration = false,
  stageKey
}: {
  animationType: AllAnimationTypeKeys;
  animationTypeOptions: Array<AnimationTypeOption<ElementChoiceButtonProps, ElementMenuProps>>;
  children: React.ReactNode;
  currentSelectedAnimationCursor: ScribeCursorId | undefined;
  currentSelectedCursor: ScribeCursorId | undefined;
  currentSelectedDirectionOption: SelectableAnimationPositionOption | SelectableAnimationDirectionOption | undefined;
  currentSelectedDrawType: DrawType | 'mixed';
  currentSelectedSpinValue: number | undefined;
  defaultSecondaryMenu?: TransitionSubMenu<ElementChoiceButtonProps, ElementMenuProps>;
  duration: number | string;
  loops: number | string;
  onAnimationCursorChoice: (cursorId: ScribeCursorId) => void;
  onAnimationTypeChange: (params: AnimationTypeChangeParams) => void;
  onCursorChoice: (cursorId: ScribeCursorId) => void;
  onDirectionChoice: (options: SelectableAnimationPositionOption | SelectableAnimationDirectionOption) => void;
  onDrawTypeChoice: (drawType: DrawType) => void;
  onDurationChange: (params: ValueChangeParams) => void;
  onLoopsChange?: (params: ValueChangeParams) => void;
  onSpinDirectionChoice: (spinRotation: number) => void;
  showDuration: boolean;
  stageKey: typeof ENTRANCE_STAGE_KEY | typeof EMPHASIS_STAGE_KEY | typeof EXIT_STAGE_KEY;
}) => {
  const [menuOpen, setMenuOpen] = useState<MenuConfig | null>(null);
  const stateCursors = useTypedSelector(state => state.cursors);
  const eraseCursors = stateCursors.handErase;

  const tweenKey = getStageTweenKey(stageKey);

  const {
    title: animationTypeTitle,
    Icon,
    secondaryMenu,
    tertiaryMenu
  } = animationTypeOptions.find(opt => opt.type === animationType) ?? {
    title: 'Mixed',
    Icon: () => <IconIndeterminate />,
    // Used for mixed animation types where secondary menu is available and is for 'Draw style'
    ...(defaultSecondaryMenu && { secondaryMenu: defaultSecondaryMenu }),
    tertiaryMenu: undefined
  };

  const reverseIcons = REVERSE_ICON_LIST.includes(animationType);

  const directionMenuOptions =
    tweenKey !== 'emphasisTween' ? selectableEntranceAnimationOptions : selectableShakeAnimationOptions;

  const handleAnimationTypeChange = (newAnimationType: AllAnimationTypeKeys) => {
    onAnimationTypeChange({ animationType: newAnimationType, stageKey });
    closeMenu();
  };

  const getSelectedSpinChoice = () => {
    const selectedOption = selectableSpinDirectionOptions.find(opt => opt.value === currentSelectedSpinValue);
    const optIcon = selectedOption !== undefined ? selectedOption.Icon() : <IconIndeterminate />;
    const optTitle = selectedOption ? selectedOption.label : 'Mixed';

    return {
      label: optTitle,
      icon: optIcon
    };
  };

  const handleSpinDirectionChoice = (spinRotation: number) => {
    onSpinDirectionChoice(spinRotation);
    closeMenu();
  };

  const getSelectedDrawTypeChoice = () => {
    const title = selectableDrawTypeOptions.find(option => option.value === currentSelectedDrawType)?.label ?? 'Mixed';
    const Icon =
      selectableDrawTypeOptions.find(option => option.value === currentSelectedDrawType)?.Icon ?? IconIndeterminate;

    return {
      label: title,
      icon: <Icon />
    };
  };

  const getSelectedCursorChoice = () => {
    const cursorId = currentSelectedCursor;

    let cursor;
    if (cursorId && cursorId !== null && typeof cursorId === 'number') {
      cursor = findCursorById(stateCursors, cursorId);
    }
    if (!cursor && cursorId === DEFAULT_HAND_CURSOR_ID) {
      cursor = rosieDefaultCursor;
    }
    if (!cursor && cursorId === DEFAULT_PEN_CURSOR_ID) {
      cursor = penDefaultCursor;
    }
    if (!cursor && cursorId === DEFAULT_DRAG_CURSOR_ID) {
      cursor = dragDefaultCursor;
    }

    const optIcon = cursor ? (
      <ChoiceButtonIcon imgUrl={cursor.resources.thumbnail.url} isPen={cursor.group === 'pens'} />
    ) : (
      <IconIndeterminate />
    );
    const optTitle = cursor ? cursor.name : 'Mixed';

    return {
      label: optTitle,
      icon: optIcon
    };
  };

  const getSelectedAnimationCursorChoice = () => {
    const cursorId = currentSelectedAnimationCursor;
    let cursor: CursorOption | undefined = eraseCursors.find(cur => cur.id === cursorId);

    if (!cursor && cursorId === DEFAULT_ERASE_CURSOR_ID) {
      cursor = defaultEraseCursor;
    } else if (!cursor && cursorId === NO_CURSOR_ID) {
      cursor = {
        id: cursorId,
        group: 'none',
        name: 'No Hand'
      };
    }

    const optIcon = getCursorIcon(cursor);
    const optTitle = cursor ? cursor.name : 'Mixed';

    return {
      label: optTitle,
      icon: optIcon
    };
  };

  const getSelectedDirectionChoice = () => {
    const selectedAnimationOption = currentSelectedDirectionOption;
    const optIcon =
      selectedAnimationOption !== undefined ? (
        selectedAnimationOption.Icon?.({ flipImage: reverseIcons })
      ) : (
        <IconIndeterminate />
      );
    const optTitle = selectedAnimationOption ? selectedAnimationOption.label : 'Mixed';
    return {
      label: optTitle,
      icon: optIcon ?? <IconIndeterminate />
    };
  };

  const closeMenu = () => {
    setMenuOpen(null);
  };

  const handleDirectionChoice = (option: SelectableAnimationPositionOption | SelectableAnimationDirectionOption) => {
    onDirectionChoice(option);
    closeMenu();
  };

  const handleCursorChoice = (cursorId: ScribeCursorId) => {
    onCursorChoice(cursorId);
    closeMenu();
  };

  const handleAnimationCursorChoice = (cursorId: ScribeCursorId) => {
    onAnimationCursorChoice(cursorId);
    closeMenu();
  };

  const handleDrawTypeChoice = (drawType: DrawType) => {
    onDrawTypeChoice(drawType);
    closeMenu();
  };

  const value = {
    animationType,
    animationTypeOptions,
    animationTypeTitle,
    closeMenu,
    currentSelectedCursor,
    currentSelectedDrawType,
    currentSelectedSpinValue,
    directionMenuOptions,
    duration,
    getSelectedAnimationCursorChoice,
    getSelectedCursorChoice,
    getSelectedDirectionChoice,
    getSelectedDrawTypeChoice,
    getSelectedSpinChoice,
    Icon,
    loops,
    menuOpen,
    onAnimationCursorChoice: handleAnimationCursorChoice,
    onAnimationTypeChange: handleAnimationTypeChange,
    onCursorChoice: handleCursorChoice,
    onDirectionChoice: handleDirectionChoice,
    onDrawTypeChoice: handleDrawTypeChoice,
    onDurationChange,
    onLoopsChange,
    onSpinDirectionChoice: handleSpinDirectionChoice,
    reverseIcons,
    secondaryMenu,
    setMenuOpen,
    showDuration,
    stageKey,
    tertiaryMenu
  };

  return <AnimationStageContext.Provider value={value}>{children}</AnimationStageContext.Provider>;
};

export const useAnimationStageContext = () => {
  return useContext(AnimationStageContext);
};
